import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ProfessionalReviewDisplay from '../components/profile/ProfessionalReviewDisplay';
import { getUserSession, isSuperAdmin, getUserDisplayName } from '../services/authService';
import { fetchAdminRoles, fetchAdminUsers, saveAdminUser, unlinkAdminSpecialistTerapias } from '../services/adminService';
import AdminPreapproveSpecialists from '../components/admin/AdminPreapproveSpecialists';
import AdminFichaEditor from '../components/admin/AdminFichaEditor';
import { fetchFicha, saveFicha } from '../services/fichaService';
import { fetchProfessionalReview } from '../services/professionalReviewService';
import { getPersonNameError, sanitizePersonNameInput } from '../utils/personName';
import '../styles/admin.css';

const emptyForm = {
  usuario_id: 0,
  planilla_id: 0,
  email: '',
  password: '123456789',
  nombre: '',
  telefono: '',
  documento_tipo: 'cedula',
  documento_numero: '',
  tipo: 'administrativo',
  rol_codigo: 'super_admin',
  activo: true,
  vincular_terapias: true,
  terapias_vinculado: false,
  terapias_nombre: '',
};

const defaultRoleByTipo = {
  administrativo: 'super_admin',
  especialista: 'especialista',
  paciente: 'paciente',
};

const AdminUsersPage = () => {
  const history = useHistory();
  const session = getUserSession();
  const sessionId = session?.id ?? null;
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [specialistReview, setSpecialistReview] = useState(null);
  const [loadingSpecialistReview, setLoadingSpecialistReview] = useState(false);
  const [specialistReviewError, setSpecialistReviewError] = useState('');
  const [preapproveOpen, setPreapproveOpen] = useState(false);
  const [fichaUsuario, setFichaUsuario] = useState(null);
  const [ficha, setFicha] = useState(null);
  const [cargandoFicha, setCargandoFicha] = useState(false);
  const [guardandoFicha, setGuardandoFicha] = useState(false);
  const [errorFicha, setErrorFicha] = useState('');
  const [avisoFicha, setAvisoFicha] = useState('');

  const isEditing = form.usuario_id > 0;
  const linkTerapias = form.tipo === 'especialista' && form.vincular_terapias;
  const showDocumentFields = form.tipo === 'especialista' || form.tipo === 'administrativo';
  const documentLabel = linkTerapias ? 'Cédula en Terapias' : 'Documento de identidad';

  const rolesForTipo = useMemo(
    () => roles.filter((role) => role.tipo === form.tipo),
    [roles, form.tipo]
  );

  const getDefaultRoleForTipo = useCallback(
    (tipo) => {
      const match = roles.find((role) => role.tipo === tipo);
      return match ? match.codigo : defaultRoleByTipo[tipo] || 'paciente';
    },
    [roles]
  );

  const loadData = useCallback(() => {
    if (!sessionId) {
      return Promise.resolve();
    }

    setLoading(true);
    setError('');

    return Promise.all([fetchAdminUsers(sessionId), fetchAdminRoles(sessionId)])
      .then(([usersList, rolesList]) => {
        setUsers(usersList);
        setRoles(rolesList);
      })
      .catch((requestError) => {
        setError(requestError.message || 'No se pudo cargar el panel.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      history.replace('/iniciar-sesion');
      return;
    }

    const currentSession = getUserSession();
    if (!isSuperAdmin(currentSession)) {
      history.replace('/');
      return;
    }

    loadData();
  }, [history, sessionId, loadData]);

  useEffect(() => {
    if (!isEditing || form.tipo !== 'especialista' || !form.usuario_id || !sessionId) {
      setSpecialistReview(null);
      setSpecialistReviewError('');
      setLoadingSpecialistReview(false);
      return;
    }

    setLoadingSpecialistReview(true);
    setSpecialistReviewError('');

    fetchProfessionalReview(form.usuario_id, sessionId)
      .then((data) => {
        setSpecialistReview(data);
      })
      .catch((requestError) => {
        setSpecialistReview(null);
        setSpecialistReviewError(requestError.message || 'No se pudo cargar la reseña profesional.');
      })
      .finally(() => {
        setLoadingSpecialistReview(false);
      });
  }, [isEditing, form.tipo, form.usuario_id, sessionId]);

  const handleEdit = (user) => {
    setMessage('');
    setSpecialistReview(null);
    setSpecialistReviewError('');
    setForm({
      usuario_id: user.id,
      planilla_id: 0,
      email: user.email || '',
      password: '',
      nombre: getUserDisplayName(user),
      telefono: user.telefono || '',
      documento_tipo: user.documento_tipo || 'cedula',
      documento_numero: user.documento_numero || '',
      tipo: user.tipo || 'paciente',
      rol_codigo: user.roles?.[0] || 'paciente',
      activo: user.activo !== false,
      vincular_terapias: Boolean(user.terapias?.vinculado && user.terapias?.verificado),
      terapias_vinculado: Boolean(user.terapias?.vinculado && user.terapias?.verificado),
      terapias_nombre: user.terapias?.nombre || '',
    });
  };

  const handleNew = () => {
    setMessage('');
    setSpecialistReview(null);
    setSpecialistReviewError('');
    setForm(emptyForm);
  };

  const handleChange = (field) => (event) => {
    let value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (field === 'nombre') {
      value = sanitizePersonNameInput(value);
    }
    setForm((current) => {
      if (field === 'tipo') {
        return {
          ...current,
          tipo: value,
          rol_codigo: getDefaultRoleForTipo(value),
        };
      }

      return { ...current, [field]: value };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setError('');
    setMessage('');

    const nameError = getPersonNameError(form.nombre);
    if (nameError && form.nombre.trim()) {
      setError(nameError);
      return;
    }

    setSaving(true);

    const payload = {
      usuario_id: form.usuario_id,
      email: form.email,
      nombre: form.nombre,
      apellido: '',
      telefono: form.telefono,
      documento_tipo: form.documento_tipo,
      documento_numero: form.documento_numero,
      tipo: form.tipo,
      rol_codigo: form.rol_codigo,
      activo: form.activo,
      vincular_terapias: form.tipo === 'especialista' ? form.vincular_terapias : false,
      planilla_id: !isEditing ? form.planilla_id || 0 : 0,
    };

    if (!isEditing) {
      payload.password = '123456789';
    } else if (form.password) {
      payload.password = form.password;
    }

    saveAdminUser(sessionId, payload)
      .then((result) => {
        setMessage(
          result && result.terapias_vinculado
            ? `Especialista vinculado con Terapias${result.terapias_nombre ? `: ${result.terapias_nombre}` : ''}.`
            : isEditing
            ? 'Usuario actualizado.'
            : 'Usuario creado. Deberá completar su perfil al iniciar sesión.'
        );
        setForm(emptyForm);
        return loadData();
      })
      .catch((requestError) => {
        setError(requestError.message || 'No se pudo guardar el usuario.');
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const handleUnlinkTerapias = () => {
    if (!sessionId || !isEditing) {
      return;
    }

    const confirmed = window.confirm(
      '¿Desvincular este especialista de Terapias? La agenda online dejará de bloquear horas ocupadas en Terapias.'
    );

    if (!confirmed) {
      return;
    }

    setUnlinking(true);
    setError('');
    setMessage('');

    unlinkAdminSpecialistTerapias(sessionId, form.usuario_id)
      .then(() => {
        setMessage('Especialista desvinculado de Terapias.');
        setForm((current) => ({
          ...current,
          terapias_vinculado: false,
          terapias_nombre: '',
        }));
        return loadData();
      })
      .catch((requestError) => {
        setError(requestError.message || 'No se pudo desvincular el especialista.');
      })
      .finally(() => {
        setUnlinking(false);
      });
  };

  const abrirFicha = (user) => {
    setFichaUsuario(user);
    setFicha(null);
    setErrorFicha('');
    setAvisoFicha('');
    setCargandoFicha(true);

    fetchFicha(user.id)
      .then((data) => {
        setFicha(data);
      })
      .catch((requestError) => {
        setErrorFicha(requestError.message || 'No se pudo cargar la ficha.');
      })
      .finally(() => {
        setCargandoFicha(false);
      });
  };

  const guardarFicha = (detalle) => {
    if (!fichaUsuario) {
      return;
    }

    setGuardandoFicha(true);
    setErrorFicha('');
    setAvisoFicha('');

    saveFicha(sessionId, fichaUsuario.id, detalle)
      .then((data) => {
        setFicha(data);
        setAvisoFicha('Ficha guardada.');
      })
      .catch((requestError) => {
        setErrorFicha(requestError.message || 'No se pudo guardar la ficha.');
      })
      .finally(() => {
        setGuardandoFicha(false);
      });
  };

  if (!session?.id || !isSuperAdmin(session)) {
    return null;
  }

  return (
    <div className="admin-root">
      <section className="admin-section admin-section--wide">
        <header className="admin-header">
          <div>
            <h1>Administración de usuarios</h1>
            <p>
              Crea cuentas con correo y contraseña. Para especialistas, registra la cédula para
              vincular Terapias; el resto de datos los completa al iniciar sesión.{' '}
              <a href="/admin/pacientes">Buscar pacientes</a>.
            </p>
          </div>
          <div className="admin-header-actions">
            <button type="button" className="admin-btn admin-btn--secondary" onClick={() => setPreapproveOpen(true)}>
              Preaprobar especialistas
            </button>
            <button type="button" className="admin-btn admin-btn--secondary" onClick={handleNew}>
              Nuevo usuario
            </button>
          </div>
        </header>

        {error ? (
          <div className="admin-alert admin-alert--error" role="alert">
            {error}
          </div>
        ) : null}

        {message ? (
          <div className="admin-alert admin-alert--success" role="status">
            {message}
          </div>
        ) : null}

        <div className="admin-layout admin-layout--users">
          <div className="admin-card">
            <h2>{isEditing ? 'Editar usuario' : 'Crear usuario'}</h2>
            <form className="admin-form" onSubmit={handleSubmit}>
              {form.planilla_id > 0 && !isEditing ? (
                <p className="admin-muted">
                  Planilla de Mi Perfil lista. Elige si es especialista o administrativo y pulsa Crear usuario.
                  El resto de la planilla se copia en ese momento.
                </p>
              ) : null}
              <label className="admin-field">
                <span>Correo electrónico</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  readOnly={form.planilla_id > 0 && form.email !== ''}
                  required
                />
              </label>

              <label className="admin-field">
                <span>{isEditing ? 'Nueva contraseña (opcional)' : 'Contraseña inicial'}</span>
                <input
                  type="text"
                  value={isEditing ? form.password : '123456789'}
                  onChange={handleChange('password')}
                  readOnly={!isEditing}
                  required={!isEditing}
                  minLength={6}
                />
              </label>
              {!isEditing ? (
                <p className="admin-muted">
                  La clave inicial es 123456789. Al crear la cuenta se envía por correo y se le pide que la
                  actualice en el primer ingreso.
                </p>
              ) : null}

              <label className="admin-field">
                <span>Nombre completo</span>
                <input
                  type="text"
                  value={form.nombre}
                  onChange={handleChange('nombre')}
                  readOnly={form.planilla_id > 0 && form.nombre !== ''}
                  required={form.tipo !== 'especialista'}
                  placeholder={linkTerapias ? 'Opcional si Terapias lo trae al vincular' : ''}
                />
              </label>

              <label className="admin-field">
                <span>Teléfono (opcional al crear)</span>
                <input
                  type="tel"
                  value={form.telefono}
                  onChange={handleChange('telefono')}
                  readOnly={form.planilla_id > 0 && form.telefono !== ''}
                />
              </label>

              <div className="admin-form-row">
                <label className="admin-field">
                  <span>Tipo de cuenta</span>
                  <select value={form.tipo} onChange={handleChange('tipo')}>
                    <option value="administrativo">Administrativo</option>
                    <option value="especialista">Especialista</option>
                    <option value="paciente">Paciente</option>
                  </select>
                </label>
                <label className="admin-field">
                  <span>Rol</span>
                  <select value={form.rol_codigo} onChange={handleChange('rol_codigo')}>
                    {rolesForTipo.map((role) => (
                      <option key={role.codigo} value={role.codigo}>
                        {role.nombre}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {form.tipo === 'especialista' ? (
                <label className="admin-checkbox">
                  <input
                    type="checkbox"
                    checked={form.vincular_terapias}
                    onChange={handleChange('vincular_terapias')}
                  />
                  Este especialista atiende en Terapias (verificar y vincular)
                </label>
              ) : null}

              {showDocumentFields ? (
                <div className="admin-form-row">
                  <label className="admin-field">
                    <span>Tipo documento</span>
                    <select
                      value={form.documento_tipo}
                      onChange={handleChange('documento_tipo')}
                      disabled={form.planilla_id > 0 && form.documento_numero !== ''}
                    >
                      <option value="cedula">Cédula</option>
                      <option value="pasaporte">Pasaporte</option>
                      <option value="otro">Otro</option>
                    </select>
                  </label>
                  <label className="admin-field">
                    <span>{documentLabel}</span>
                    <input
                      type="text"
                      value={form.documento_numero}
                      onChange={handleChange('documento_numero')}
                      readOnly={form.planilla_id > 0 && form.documento_numero !== ''}
                      required={form.tipo === 'especialista'}
                      placeholder={linkTerapias ? 'Ej. 8-906-250' : 'Documento del usuario'}
                    />
                  </label>
                </div>
              ) : null}

              {isEditing && form.terapias_vinculado ? (
                <div className="admin-terapias-panel">
                  <p className="admin-terapias-status">
                    Vinculado con Terapias
                    {form.terapias_nombre ? `: ${form.terapias_nombre}` : ''}.
                    {form.documento_numero ? ` Documento: ${form.documento_numero}.` : ''}
                  </p>
                  <button
                    type="button"
                    className="admin-btn admin-btn--danger"
                    onClick={handleUnlinkTerapias}
                    disabled={saving || unlinking}
                  >
                    {unlinking ? 'Desvinculando…' : 'Desvincular de Terapias'}
                  </button>
                </div>
              ) : null}

              {isEditing && form.tipo === 'especialista' ? (
                <div className="admin-review-panel">
                  <h3 className="admin-review-title">Reseña profesional</h3>
                  <p className="admin-review-note">
                    Solo lectura. El especialista guarda borrador o publica desde su perfil.
                  </p>
                  {loadingSpecialistReview ? (
                    <p className="admin-muted">Cargando reseña…</p>
                  ) : null}
                  {!loadingSpecialistReview && specialistReviewError ? (
                    <p className="admin-alert admin-alert--error">{specialistReviewError}</p>
                  ) : null}
                  {!loadingSpecialistReview && !specialistReviewError ? (
                    <ProfessionalReviewDisplay
                      review={specialistReview}
                      specialistName={form.nombre}
                      statusLabel={
                        specialistReview && specialistReview.publicada
                          ? 'Publicada — visible en agenda'
                          : 'Borrador — no visible para pacientes'
                      }
                    />
                  ) : null}
                </div>
              ) : null}

              <label className="admin-checkbox">
                <input type="checkbox" checked={form.activo} onChange={handleChange('activo')} />
                Usuario activo
              </label>

              <button type="submit" className="admin-btn admin-btn--primary" disabled={saving || unlinking}>
                {saving ? 'Guardando…' : isEditing ? 'Actualizar usuario' : 'Crear usuario'}
              </button>
            </form>
          </div>

          <div className="admin-card admin-card--table">
            <h2>Usuarios registrados</h2>
            {loading ? <p className="admin-muted">Cargando…</p> : null}
            {!loading ? (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Correo</th>
                      <th>Tipo</th>
                      <th>Documento</th>
                      <th>Terapias</th>
                      <th>Roles</th>
                      <th>Estado</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>{getUserDisplayName(user)}</td>
                        <td>{user.email}</td>
                        <td>{user.tipo}</td>
                        <td>{user.documento_numero || '—'}</td>
                        <td>
                          {user.terapias && user.terapias.vinculado && user.terapias.verificado
                            ? user.terapias.nombre || 'Vinculado'
                            : '—'}
                        </td>
                        <td>{(user.roles || []).join(', ')}</td>
                        <td>{user.activo ? 'Activo' : 'Inactivo'}</td>
                        <td className="admin-acciones">
                          <button
                            type="button"
                            className="admin-link-btn"
                            onClick={() => handleEdit(user)}
                          >
                            Editar
                          </button>
                          {user.tipo === 'especialista' || user.tipo === 'administrativo' ? (
                            <button type="button" className="admin-link-btn" onClick={() => abrirFicha(user)}>
                              Editar todo
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      </section>
      {preapproveOpen ? (
        <AdminPreapproveSpecialists
          solicitanteId={sessionId}
          onClose={() => setPreapproveOpen(false)}
          onUsar={(item) => {
            const cedula = (item.cedula || '').trim();
            const pasaporte = (item.pasaporte || '').trim();
            setPreapproveOpen(false);
            setError('');
            setSpecialistReview(null);
            setForm({
              ...emptyForm,
              email: item.correo || '',
              password: '123456789',
              nombre: item.nombre || '',
              telefono: item.telefono || '',
              documento_tipo: cedula ? 'cedula' : pasaporte ? 'pasaporte' : 'cedula',
              documento_numero: cedula || pasaporte,
              tipo: 'especialista',
              rol_codigo: getDefaultRoleForTipo('especialista'),
              activo: true,
              vincular_terapias: false,
              planilla_id: item.id,
            });
            setMessage('Datos de la planilla listos. Elige el tipo de cuenta y pulsa Crear usuario.');
          }}
        />
      ) : null}
      {fichaUsuario ? (
        <AdminFichaEditor
          usuarioNombre={getUserDisplayName(fichaUsuario)}
          ficha={ficha}
          cargando={cargandoFicha}
          error={errorFicha}
          aviso={avisoFicha}
          guardando={guardandoFicha}
          onClose={() => {
            setFichaUsuario(null);
            setFicha(null);
            setErrorFicha('');
            setAvisoFicha('');
          }}
          onSave={guardarFicha}
        />
      ) : null}
    </div>
  );
};

export default AdminUsersPage;
